import { useState } from 'react';
import { NavLink as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from '../../../../hooks/useAuth';
import { useSelector, useDispatch } from '../../../../redux/store';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Badge,
  BottomNavigationAction,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Menu,
  MenuItem
} from '@mui/material';
import { MIcon } from '../../../@material-extend';
import { getResourceFilesNotifications } from '../../../../redux/slices/files';
import { setDateDashboard } from '../../../../redux/slices/dashboard';
import { setDateResources } from '../../../../redux/slices/resources';
import { setDateSites } from '../../../../redux/slices/sites';
import moment from 'moment';

const NAV_BOTTOM_ITEM_MIN_HEIGHT = 88;
const NAV_BOTTOM_ITEM_MIN_WIDTH = 64;

const NavItem = (props) => {
  const {
    item: { title, mobileTitle, mobileIcon, path, id, icon, menuItems },
    isCollapse,
    isMobile,
    number = null
  } = props;

  const env = typeof window !== 'undefined' && window.env ? window.env : {};

  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const displayTitle = isMobile && mobileTitle ? mobileTitle : title;
  const displayIcon = isMobile && mobileIcon ? mobileIcon : icon;
  const showPopupMenu = isMobile && (mobileTitle || menuItems);
  const hasDesktopSubmenu = !isMobile && menuItems;

  const {
    palette: {
      action: { selectedOpacity },
      primary: { main, darker }
    },
    spacing
  } = useTheme();

  const dispatch = useDispatch();

  const { unseenResourceFilesCountGlobal, unseenFilesSiteCount, unseenCompanyFiles } = useSelector(
    (state) => state.files
  );
  const { features, isManager } = useAuth();
  const { pathname } = useLocation();
  const isActiveRoot = path ? pathname === path || pathname.startsWith(path + '/') : false;
  const isMenuItemActive = menuItems
    ? menuItems.some((mi) => pathname === mi.path || pathname.startsWith(mi.path + '/'))
    : false;
  const isActive = isActiveRoot || isMenuItemActive;

  const variant = isActive ? 'primary' : 'neutral-faded';
  const activeRootStyle = {
    backgroundColor: alpha(main, selectedOpacity),
    borderTopColor: isMobile ? main : 'transparent',
    '.MuiBottomNavigationAction-label, .MuiTypography-root': {
      color: darker,
      fontWeight: 600
    }
  };

  const getNotifCount = () => {
    if (!features?.documentConsultations) return number || 0;

    const map = {
      'sheet-nav': unseenResourceFilesCountGlobal,
      'agents-nav': unseenResourceFilesCountGlobal,
      'planning-nav': unseenFilesSiteCount,
      'provider-list-nav': unseenCompanyFiles,
      'sites-list-nav': unseenFilesSiteCount
    };

    return (map[id] ?? number) || 0;
  };

  const maybeFetchNotifications = () => {
    if (features?.documentConsultations && !isManager && !env.isClockingApp) {
      dispatch(getResourceFilesNotifications());
    }
  };

  const handleMobileClick = (event) => {
    if (showPopupMenu) {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    } else {
      maybeFetchNotifications();
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (itemPath) => {
    handleMenuClose();
    navigate(itemPath);
  };

  const handleDesktopClick = () => {
    dispatch(setDateDashboard(new Date()));
    dispatch(setDateResources(moment()));
    dispatch(setDateSites(moment().toDate()));
    if (features?.documentConsultations) {
      maybeFetchNotifications();
    }
  };

  if (isMobile) {
    const notif = getNotifCount();

    return (
      <>
        <BottomNavigationAction
          id={id}
          data-cy={`nav-item-${id}`}
          component={RouterLink}
          to={path}
          showLabel
          label={displayTitle}
          draggable="false"
          onClick={handleMobileClick}
          className="text-line-clamp-2"
          icon={
            notif > 0 ? (
              <Badge
                badgeContent={notif}
                color="error"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MIcon icon={displayIcon} variant={variant} />
              </Badge>
            ) : (
              <MIcon icon={displayIcon} variant={variant} />
            )
          }
          sx={styleBottomNav(isActive, activeRootStyle, env)}
        />

        {showPopupMenu && (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
          >
            {menuItems ? (
              menuItems.map((menuItem) => {
                const isMenuItemActive = pathname === menuItem.path || pathname.startsWith(menuItem.path + '/');
                return (
                  <MenuItem
                    key={menuItem.path}
                    onClick={() => handleMenuItemClick(menuItem.path)}
                    selected={isMenuItemActive}
                    sx={{ gap: 1 }}
                  >
                    <MIcon icon={menuItem.icon} variant={isMenuItemActive ? 'primary' : 'neutral-faded'} />
                    {menuItem.title}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem onClick={() => handleMenuItemClick(path)} sx={{ gap: 1 }}>
                <MIcon icon={icon} variant="neutral-faded" />
                {title}
              </MenuItem>
            )}
          </Menu>
        )}
      </>
    );
  }

  const notif = getNotifCount();

  if (hasDesktopSubmenu) {
    const isSubmenuActive = menuItems.some((mi) => pathname === mi.path || pathname.startsWith(mi.path + '/'));
    return (
      <>
        <ListItem sx={styleListItem(activeRootStyle, isSubmenuActive, isCollapse, spacing)} draggable="false" disablePadding>
          <ListItemButton id={id} data-cy={`nav-item-${id}`} onClick={() => setExpanded((prev) => !prev)}>
            <ListItemIcon sx={{ minWidth: 'initial' }}>
              <MIcon icon={icon} variant={isSubmenuActive ? 'primary' : variant} />
            </ListItemIcon>
            <ListItemText primary={displayTitle} />
            <MIcon icon={expanded ? 'expand_less' : 'expand_more'} sx={{ fontSize: 20, color: 'text.secondary' }} />
          </ListItemButton>
        </ListItem>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {menuItems.map((menuItem) => {
              const isMenuItemActive = pathname === menuItem.path || pathname.startsWith(menuItem.path + '/');
              return (
                <ListItem key={menuItem.path} disablePadding sx={{ pl: 2 }}>
                  <ListItemButton
                    component={RouterLink}
                    to={menuItem.path}
                    sx={{
                      minHeight: 32,
                      gap: 1.5,
                      borderRadius: (theme) => theme.shape.borderRadiusXs,
                      '.MuiListItemText-primary': {
                        fontSize: '0.8125rem',
                        color: (theme) => isMenuItemActive ? theme.palette.primary.main : theme.palette.text.secondary,
                        fontWeight: isMenuItemActive ? 600 : 400,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'initial' }}>
                      <MIcon icon={menuItem.icon} variant={isMenuItemActive ? 'primary' : 'neutral-faded'} />
                    </ListItemIcon>
                    <ListItemText primary={menuItem.title} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItem sx={styleListItem(activeRootStyle, isActive, isCollapse, spacing)} draggable="false" disablePadding>
      <ListItemButton id={id} data-cy={`nav-item-${id}`} component={RouterLink} to={path} onClick={handleDesktopClick}>
        <ListItemIcon sx={{ minWidth: 'initial' }}>
          <MIcon icon={icon} variant={variant} />
        </ListItemIcon>

        {notif > 0 ? (
          <Badge badgeContent={notif} color="error" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <ListItemText primary={displayTitle} />
          </Badge>
        ) : (
          <ListItemText primary={displayTitle} />
        )}
      </ListItemButton>
    </ListItem>
  );
};

const styleListItem = (activeRootStyle, isActiveRoot, isCollapse, spacing) => ({
  position: 'relative',
  margin: spacing(0, 1),
  width: 'auto',
  maxWidth: '100%',
  '.MuiListItemButton-root': {
    minHeight: 32,
    flexDirection: isCollapse ? 'column' : 'row',
    justifyContent: isCollapse ? 'center' : 'flex-start',
    alignItems: 'center',
    gap: isCollapse ? 0.5 : 1.5,
    borderRadius: (theme) => theme.shape.borderRadiusXs,
    ':before': {
      content: "''",
      position: 'absolute',
      top: 12,
      left: 0,
      bottom: 12,
      width: 4,
      display: !isActiveRoot ? 'none' : 'block',
      borderRadius: '0px 4px 4px 0px',
      backgroundColor: (theme) => theme.palette.primary.main
    },
    '.MuiBadge-badge': {
      lineHeight: '0px',
      top: isCollapse ? '-24px' : '11px',
      right: isCollapse ? '11px' : '-24px'
    },
    '.MuiListItemText-primary': {
      lineHeight: '1.15rem',
      color: (theme) => theme.palette.text.secondary,
      textAlign: isCollapse ? 'center' : 'left',
      ...(isActiveRoot && { fontWeight: 600 })
    },
    ...(isActiveRoot && {
      ...activeRootStyle,
      '.MuiListItemText-primary': {
        lineHeight: '1.15rem',
        fontWeight: 600,
        textAlign: isCollapse ? 'center' : 'left'
      }
    })
  }
});

const styleBottomNav = (isActiveRoot, activeRootStyle, env) => ({
  flex: '1 1 auto',
  justifyContent: 'flex-start',
  pt: 1.5,
  pb: env.isIos ? 2 : null,
  minHeight: NAV_BOTTOM_ITEM_MIN_HEIGHT,
  minWidth: NAV_BOTTOM_ITEM_MIN_WIDTH,
  borderTop: '4px solid transparent',
  '.icon': {
    flexShrink: 0,
    height: 24,
    width: 24
  },
  '.MuiBottomNavigationAction-label': {
    display: '-webkit-inline-box',
    lineClamp: '2',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    textAlign: 'center',
    whiteSpace: 'normal',
    fontSize: { xs: '0.625rem', sr: '0.75rem' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1rem'
  },
  ...(isActiveRoot && activeRootStyle)
});

NavItem.propTypes = {
  isCollapse: PropTypes.bool,
  isMobile: PropTypes.bool,
  item: PropTypes.object,
  number: PropTypes.number
};

export default NavItem;
